import type { ComputedRef } from "vue";
import type { TicketType } from "~/types/event";

/**
 * useEventAnimatedCounters — compteurs animés des places restantes
 * (global + par tarif) mis à jour en temps réel via le socket billetterie.
 * Extrait 1:1 de `pages/b/[slug]/index.vue` (Zone A split >800 lignes).
 */
export function useEventAnimatedCounters(deps: {
  totalRemainingPlaces: ComputedRef<number | null>;
  ticketTypes: ComputedRef<TicketType[]>;
  getRemainingForDisplay: (tt: TicketType) => number | null;
}) {
  const { totalRemainingPlaces, ticketTypes, getRemainingForDisplay } = deps;

  const { displayValue: animatedGlobalRemaining, setValue: setAnimatedGlobalRemaining } = useAnimatedNumber(
    totalRemainingPlaces.value ?? 0,
    600,
  );
  watch(totalRemainingPlaces, (newVal) => {
    if (newVal !== null) setAnimatedGlobalRemaining(newVal);
  });

  const animatedPerType = ref<Record<string, ReturnType<typeof useAnimatedNumber>>>({});
  watch(
    () => ticketTypes.value.map(tt => ({ id: tt.id, remaining: getRemainingForDisplay(tt) })),
    (entries) => {
      for (const entry of entries) {
        if (entry.remaining === null) continue;
        const existing = animatedPerType.value[entry.id];
        if (existing) {
          existing.setValue(entry.remaining);
        } else {
          animatedPerType.value[entry.id] = useAnimatedNumber(entry.remaining, 600);
        }
      }
    },
    { deep: true },
  );

  return { animatedGlobalRemaining, animatedPerType };
}
